import React, { useCallback } from "react"
import { useNotificationContext } from "../contexts/NotificationContext"
import { UpdateNotificationActions } from "../components/global/UpdateNotificationActions"
import logMessage from "../utils/logUtils"
import { MsFromDays, MsFromSeconds } from "../utils/timeUtils"
import {
    getUpdateDismissed,
    setUpdateDismissed,
    getUpdateShown,
    setUpdateShown,
} from "../utils/localStorage"

export const useServiceWorkerUpdate = () => {
    const { createNotification, dismissNotification } = useNotificationContext()

    const onDismissNotification = useCallback((notificationId: string) => {
        try {
            setUpdateDismissed(new Date().toISOString())
        } catch (error) {
            logMessage(
                "Failed to set update dismissal time in localStorage",
                "error",
                {
                    metadata: {
                        error:
                            error instanceof Error
                                ? error.message
                                : String(error),
                    },
                }
            )
        }
        dismissNotification(notificationId)
    }, [])

    const handleServiceWorkerUpdate = useCallback(
        (registration: ServiceWorkerRegistration) => {
            const reloadPage = (notificationId: string) => {
                try {
                    if (registration.waiting) {
                        // Set up the listener BEFORE posting the message
                        const handleControllerChange = () => {
                            navigator.serviceWorker.removeEventListener(
                                "controllerchange",
                                handleControllerChange
                            )
                            // Force a hard reload to bypass any caching issues
                            window.location.reload()
                        }

                        navigator.serviceWorker.addEventListener(
                            "controllerchange",
                            handleControllerChange
                        )

                        // Tell the new service worker to skip waiting
                        registration.waiting.postMessage({
                            type: "SKIP_WAITING",
                        })

                        // Fallback: if controllerchange doesn't fire within 5 seconds, force reload
                        setTimeout(() => {
                            console.warn(
                                "Service worker update timeout, forcing reload"
                            )
                            window.location.reload()
                        }, 5000)
                    } else {
                        // Fallback: just reload
                        window.location.reload()
                    }

                    dismissNotification(notificationId)
                } catch (error) {
                    logMessage(
                        "Failed to skip waiting for service worker",
                        "error",
                        {
                            metadata: {
                                error:
                                    error instanceof Error
                                        ? error.message
                                        : String(error),
                            },
                        }
                    )
                    dismissNotification(notificationId)
                    // Force reload on error
                    window.location.reload()
                }
            }

            const lastDismissal = getUpdateDismissed()
            if (lastDismissal) {
                const lastDismissalDate = new Date(lastDismissal)
                const now = new Date()
                const timeSinceLastDismissal =
                    now.getTime() - lastDismissalDate.getTime()

                if (timeSinceLastDismissal < MsFromDays(2)) {
                    return
                }
            }

            const lastDisplay = getUpdateShown()
            if (lastDisplay) {
                const lastDisplayDate = new Date(lastDisplay)
                const now = new Date()
                const timeSinceLastDisplay =
                    now.getTime() - lastDisplayDate.getTime()

                if (timeSinceLastDisplay < MsFromDays(2)) {
                    return
                }
            }

            try {
                setUpdateShown(new Date().toISOString())
            } catch (error) {
                logMessage(
                    "Failed to set update display time in localStorage",
                    "error",
                    {
                        metadata: {
                            error:
                                error instanceof Error
                                    ? error.message
                                    : String(error),
                        },
                    }
                )
            }

            // Create a notification ID for dismissal
            const notificationId = `sw-update-${Date.now()}`

            createNotification({
                id: notificationId,
                title: "Update Available",
                message: "A new version of DDO Audit is available.",
                type: "info",
                ttl: MsFromSeconds(10),
                actions: React.createElement(UpdateNotificationActions, {
                    onRefresh: () => reloadPage(notificationId),
                    onDismiss: () => onDismissNotification(notificationId),
                }),
            })
        },
        [createNotification, dismissNotification]
    )

    return { handleServiceWorkerUpdate }
}
